import { Component, computed, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { AuthService } from '../../../core/services/auth.service';
import { CartService } from '../../../core/services/cart.service';
import { ThemeService } from '../../../core/services/theme.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterLink, ButtonModule, MenuModule, OverlayBadgeModule],
  template: `
    <header class="sticky top-0 z-50 border-b border-surface-200 bg-surface-0/95 backdrop-blur">
      <div class="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4">
        <!-- Logótipo / Marca -->
        <a routerLink="/" class="flex items-center gap-2 whitespace-nowrap text-base font-bold text-primary sm:text-xl">
           <img src="logo.png" alt="Logo" class="mx-auto h-8 w-auto" />
          <span>Prestige Salgados</span>
        </a>

        <nav class="flex items-center gap-1 sm:gap-4">
          <!-- Link do Carrinho (o admin não faz pedidos) -->
          @if (!authService.isAdmin()) {
            <a routerLink="/checkout" aria-label="Carrinho"
               class="flex items-center gap-2 rounded-lg px-3 py-2 font-medium text-surface-700 transition-colors hover:bg-surface-100">
              @if (cartService.count() > 0) {
                <p-overlaybadge [value]="cartService.count()" severity="danger">
                  <i class="pi pi-shopping-cart text-xl"></i>
                </p-overlaybadge>
              } @else {
                <i class="pi pi-shopping-cart text-xl"></i>
              }
              <span class="hidden sm:inline">Carrinho</span>
            </a>
          }

          <!-- Alternância entre tema claro e escuro -->
          <p-button [icon]="themeService.isDark() ? 'pi pi-sun' : 'pi pi-moon'" severity="secondary" [text]="true" [rounded]="true"
                    [ariaLabel]="themeService.isDark() ? 'Mudar para o tema claro' : 'Mudar para o tema escuro'"
                    (onClick)="themeService.toggle()" />

          <!-- Área de Utilizador Autenticado -->
          @if (authService.isAuthenticated()) {
            <p-button [label]="firstName()" icon="pi pi-user" severity="secondary" [outlined]="true"
                      (onClick)="userMenu.toggle($event)" />
            <p-menu #userMenu [model]="menuItems()" [popup]="true" appendTo="body" />
          }
          <!-- Área de Visitante -->
          @else {
            <p-button label="Entrar" icon="pi pi-sign-in" (onClick)="goToLogin()" />
          }
        </nav>
      </div>
    </header>
  `
})
export class HeaderComponent {
  authService = inject(AuthService);
  cartService = inject(CartService);
  themeService = inject(ThemeService);
  private router = inject(Router);

  // Mostra apenas o primeiro nome
  firstName = computed(() => this.authService.user()?.name?.split(' ')[0] || 'Perfil');

  menuItems = computed<MenuItem[]>(() => [
    // O ADMIN só gerencia: vê o painel no lugar dos pedidos próprios
    this.authService.isAdmin()
      ? { label: 'Painel admin', icon: 'pi pi-cog', routerLink: '/admin' }
      : { label: 'Meus pedidos', icon: 'pi pi-receipt', routerLink: '/my-orders' },
    { label: 'Meus dados', icon: 'pi pi-user-edit', routerLink: '/profile' },
    { separator: true },
    { label: 'Sair', icon: 'pi pi-sign-out', command: () => this.logout() }
  ]);

  goToLogin() {
    this.router.navigate(['/login']);
  }

  logout() {
    this.authService.logout();
    this.router.navigate(['/']);
  }
}
